// frontend/src/pages/doctor/PatientRecords.js
import React, { useState, useEffect } from 'react';
import { Search, User, Calendar, FileText, Eye } from 'lucide-react';
import doctorService from '../../services/doctorService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const PatientRecords = () => {
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [filteredConsultations, setFilteredConsultations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConsultations();
  }, []);

  useEffect(() => {
    filterConsultations();
  }, [searchTerm, consultations]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await doctorService.getConsultations({
        status: 'completed',
        limit: 200
      });

      if (response.success) {
        setConsultations(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch patient records');
    } finally {
      setLoading(false);
    }
  };

  const filterConsultations = () => {
    if (!searchTerm.trim()) {
      setFilteredConsultations(consultations);
      return;
    }

    const filtered = consultations.filter(
      consultation =>
        consultation.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consultation.symptoms?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consultation.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredConsultations(filtered);
  };

  const openRecordModal = (consultation) => {
    setSelectedRecord(consultation);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  // Group consultations by patient
  const groupedByPatient = {};
  filteredConsultations.forEach(consultation => {
    const patientId = consultation.patient?._id || 'anonymous';
    const patientName = consultation.patient?.name || 'Anonymous Patient';
    
    if (!groupedByPatient[patientId]) {
      groupedByPatient[patientId] = {
        id: patientId,
        name: patientName,
        phone: consultation.patient?.phone || 'N/A',
        consultations: []
      };
    }
    
    groupedByPatient[patientId].consultations.push(consultation);
  });

  const patients = Object.values(groupedByPatient).sort((a, b) => 
    b.consultations.length - a.consultations.length
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Patient Records</h1>
        <p className="text-gray-600 mt-1">View consultation history and patient information</p>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by patient name, symptoms, or diagnosis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Patients</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{patients.length}</p>
            </div>
            <div className="bg-blue-500 p-3 rounded-lg">
              <User className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Consultations</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{consultations.length}</p>
            </div>
            <div className="bg-green-500 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Avg. Consultations/Patient</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {patients.length > 0 ? (consultations.length / patients.length).toFixed(1) : 0}
              </p>
            </div>
            <div className="bg-purple-500 p-3 rounded-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Patient List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Patient Consultation History</h3>
        </div>

        {patients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No patient records found</p>
            <p className="text-sm mt-1">Complete consultations to build patient records</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {patients.map((patient) => (
              <div key={patient.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{patient.name}</h4>
                        <p className="text-sm text-gray-600">{patient.phone}</p>
                      </div>
                    </div>

                    <div className="ml-13">
                      <p className="text-sm text-gray-600 mb-3">
                        <strong>{patient.consultations.length}</strong> consultation
                        {patient.consultations.length !== 1 ? 's' : ''} completed
                      </p>

                      {/* Recent Consultations */}
                      <div className="space-y-2">
                        {patient.consultations.slice(0, 3).map((consultation) => (
                          <div
                            key={consultation._id}
                            className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => openRecordModal(consultation)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-xs text-gray-500">
                                    {new Date(consultation.completedAt).toLocaleDateString()}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      consultation.type === 'online'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-green-100 text-green-700'
                                    }`}
                                  >
                                    {consultation.type}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">
                                  <strong>Symptoms:</strong> {consultation.symptoms.substring(0, 60)}
                                  {consultation.symptoms.length > 60 ? '...' : ''}
                                </p>
                                {consultation.diagnosis && (
                                  <p className="text-sm text-gray-700 mt-1">
                                    <strong>Diagnosis:</strong>{' '}
                                    {consultation.diagnosis.substring(0, 60)}
                                    {consultation.diagnosis.length > 60 ? '...' : ''}
                                  </p>
                                )}
                              </div>
                              <button className="ml-3 text-primary-600 hover:text-primary-800">
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {patient.consultations.length > 3 && (
                        <button className="mt-2 text-sm text-primary-600 hover:text-primary-800">
                          View all {patient.consultations.length} consultations →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Record Detail Modal */}
      {showModal && selectedRecord && (
        <Modal
          isOpen={showModal}
          onClose={closeModal}
          title="Consultation Record"
          size="large"
        >
          <div className="space-y-4">
            {/* Patient Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Patient Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedRecord.patient?.name || 'Anonymous'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedRecord.patient?.phone || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Consultation Type</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {selectedRecord.type}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(selectedRecord.completedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Consultation Details */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Symptoms</h4>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                {selectedRecord.symptoms}
              </p>
            </div>

            {selectedRecord.diagnosis && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Diagnosis</h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                  {selectedRecord.diagnosis}
                </p>
              </div>
            )}

            {selectedRecord.prescription && selectedRecord.prescription.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Prescription</h4>
                <ul className="list-disc list-inside space-y-1 bg-gray-50 p-3 rounded">
                  {selectedRecord.prescription.map((med, index) => (
                    <li key={index} className="text-sm text-gray-700">
                      {med}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedRecord.recommendations && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Recommendations</h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                  {selectedRecord.recommendations}
                </p>
              </div>
            )}

            {selectedRecord.followUpDate && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Follow-up Date</h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                  {new Date(selectedRecord.followUpDate).toLocaleDateString()}
                </p>
              </div>
            )}

            {selectedRecord.doctorNotes && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Doctor's Notes</h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                  {selectedRecord.doctorNotes}
                </p>
              </div>
            )}

            {/* Consultation Fee */}
            <div className="bg-blue-50 p-4 rounded-lg flex justify-between items-center">
              <span className="font-semibold text-gray-900">Consultation Fee</span>
              <span className="text-lg font-bold text-primary-600">
                KES {selectedRecord.fee.toLocaleString()}
              </span>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PatientRecords;